import {
  useAssetStatusList,
  useAssetType,
  useFacilitiesList,
  useSystemOptions,
} from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { X } from "lucide-react";
import { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

interface AssetSearchFiltersType {
  assetType: string;
  facility: string;
  system: string;
  status: string;
  page: number;
}

interface AssetSearchFiltersProps {
  filters: AssetSearchFiltersType;
  onFiltersChange: (filters: AssetSearchFiltersType) => void;
  onApplyFilters: (filters: AssetSearchFiltersType) => void;
  onClearFilters: () => void;
  loading?: boolean;
}

export const AssetSearchFilters = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  loading = false,
}: AssetSearchFiltersProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch options from hooks
  const { data: assetTypeList } = useAssetType();
  const { data: facilityList } = useFacilitiesList();
  const { data: systemList } = useSystemOptions();
  const { data: assetStatusList } = useAssetStatusList();

  // Transform asset type options
  const assetTypeOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Types" }];
    const apiOptions =
      assetTypeList?.result?.map((assetType: any) => ({
        value: assetType.code,
        label: assetType.name,
      })) || [];
    return [...options, ...apiOptions];
  }, [assetTypeList]);

  // Transform facility options
  const facilityOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Facilities" }];
    const apiOptions =
      facilityList?.map((facility: any) => ({
        value: facility.id,
        label: facility.facilityName,
      })) || [];
    return [...options, ...apiOptions];
  }, [facilityList]);

  // Transform system options
  const systemOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Systems" }];
    const apiOptions =
      systemList?.map((system: any) => ({
        value: system.id,
        label: system.systemName,
      })) || [];
    return [...options, ...apiOptions];
  }, [systemList]);

  // Transform asset status options
  const assetStatusOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Statuses" }];
    const apiOptions =
      assetStatusList?.result?.map((status: any) => ({
        value: status.key,
        label: status.value,
      })) || [];
    return [...options, ...apiOptions];
  }, [assetStatusList]);

  // Parse current URL parameters
  const getCurrentFiltersFromUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      assetType: searchParams.get("assetType") || "all",
      facility: searchParams.get("facility") || "all",
      system: searchParams.get("system") || "all",
      status: searchParams.get("status") || "all",
      page: 1,
    };
  }, [location.search]);

  // Define form fields
  const createFormFields = (): FormField[] => [
    {
      name: "assetType",
      label: "Asset Type",
      type: "select",
      required: false,
      defaultValue: getCurrentFiltersFromUrl.assetType,
      options: assetTypeOptions,
      classes: FIELD_CLASSES,
    },
    {
      name: "facility",
      label: "Facility",
      type: "select",
      required: false,
      defaultValue: getCurrentFiltersFromUrl.facility,
      options: facilityOptions,
      classes: FIELD_CLASSES,
    },
    {
      name: "system",
      label: "System",
      type: "select",
      required: false,
      defaultValue: getCurrentFiltersFromUrl.system,
      options: systemOptions,
      classes: FIELD_CLASSES,
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      required: false,
      defaultValue: getCurrentFiltersFromUrl.status,
      options: assetStatusOptions,
      classes: FIELD_CLASSES,
    },
  ];

  // Create form instance
  const filterForm: UseFormReturn<AssetSearchFiltersType> =
    FormService.createForm<AssetSearchFiltersType>({
      fields: createFormFields(),
    });

  // Update URL with filters
  const updateUrlWithFilters = (newFilters: AssetSearchFiltersType) => {
    const searchParams = new URLSearchParams(location.search);

    // Clear existing filter parameters
    const filterParams = ["assetType", "facility", "system", "status"];
    filterParams.forEach((param) => searchParams.delete(param));

    // Add new filter parameters (only non-'all' values)
    Object.entries(newFilters).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        value !== "all"
      ) {
        searchParams.set(key, String(value));
      }
    });

    // Always reset to page 1 when filters change
    searchParams.set("page", "1");

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  // Handle form submission (Apply Filters)
  const handleSubmit = (data: any) => {
    const processedData = { ...data, page: 1 };

    updateUrlWithFilters(processedData);

    onApplyFilters(processedData);
  };

  // Handle form reset (Clear Filters)
  const handleReset = () => {
    const defaultFilters: AssetSearchFiltersType = {
      assetType: "all",
      facility: "all",
      system: "all",
      status: "all",
      page: 1,
    };

    filterForm.reset(defaultFilters);

    // Just notify parent - let parent handle URL updates
    onClearFilters();
  };

  // Sync form with URL on mount and URL changes
  useEffect(() => {
    const urlFilters = getCurrentFiltersFromUrl;
    Object.keys(urlFilters).forEach((key) => {
      const value = urlFilters[key as keyof typeof urlFilters];
      if (value !== undefined) {
        filterForm.setValue(key as keyof AssetSearchFiltersType, value);
      }
    });
  }, [getCurrentFiltersFromUrl, filterForm]);

  // Custom footer with Apply and Clear buttons
  const renderCustomFooter = () => (
    <div className="flex justify-between items-center pt-6 border-t border-gray-200">
      <Button
        type="button"
        variant="outline"
        onClick={handleReset}
        disabled={loading}
      >
        <X className="h-4 w-4 mr-2" />
        Clear Filters
      </Button>

      <Button
        isLoading={loading}
        onClick={() => handleSubmit(filterForm.getValues())}
      >
        Apply Filters
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={createFormFields()}
        form={filterForm}
        // onReset={handleReset}
        loading={loading}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          layout: "vertical",
          spacing: "medium",
        }}
        gridColumns={4}
        className="space-y-6"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
